import React, { useState, useEffect, useMemo } from 'react';

import { 
    collection, 
    query, 
    orderBy, 
    onSnapshot, 
    doc, 
    setDoc, 
    addDoc, 
    deleteDoc, 
    updateDoc, 
    serverTimestamp 
 } from 'firebase/firestore';
import { GEMINI_API_KEY } from '../../config/firebase';

export default function BudgetTracker({ user, db, eventId }) {
    const [items, setItems] = useState([]);
    const [totalBudget, setTotalBudget] = useState(0);
    const [isEditingBudget, setIsEditingBudget] = useState(false);
    const [newItem, setNewItem] = useState({ name: '', category: 'General', estimated: '', actual: '', paid: false });
    const [loading, setLoading] = useState(true);
    
    // AI State
    const [showAiModal, setShowAiModal] = useState(false);
    const [aiEventType, setAiEventType] = useState('');
    const [aiGuestCount, setAiGuestCount] = useState('');
    const [aiBudgetLevel, setAiBudgetLevel] = useState('Standard'); // Budget, Standard, Luxury
    const [isGenerating, setIsGenerating] = useState(false);

    const categories = ['Venue', 'Food & Drink', 'Decor', 'Entertainment', 'Attire', 'Photo/Video', 'Stationery', 'Favors', 'General'];

    // Construct paths dynamically based on eventId
    const settingsPath = useMemo(() => {
        return eventId 
            ? `users/${user.uid}/events/${eventId}/budget/settings`
            : `users/${user.uid}/budget/settings`;
    }, [user.uid, eventId]);

    const itemsPath = useMemo(() => {
        return eventId 
            ? `users/${user.uid}/events/${eventId}/budgetItems`
            : `users/${user.uid}/budgetItems`;
    }, [user.uid, eventId]);

    useEffect(() => {
        if (!user) return;
        
        // Fetch Settings (Total Budget) - Keep settings in the separate 'budget' doc structure
        const settingsRef = doc(db, settingsPath);
        const unsubSettings = onSnapshot(settingsRef, (docSnap) => {
            if (docSnap.exists()) {
                setTotalBudget(docSnap.data().totalBudget || 0);
            }
        }, (error) => {
            if (error.code !== 'permission-denied') console.error("Budget settings error:", error);
        });

        // Fetch Budget Items
        const itemsRef = collection(db, itemsPath);
        const q = query(itemsRef, orderBy('createdAt', 'desc'));
        const unsubItems = onSnapshot(q, (snapshot) => {
            setItems(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        }, (error) => {
            if (error.code !== 'permission-denied') console.error("Budget items error:", error);
        });

        return () => { unsubSettings(); unsubItems(); };
    }, [user, db, settingsPath, itemsPath]);

    const handleUpdateBudget = async () => {
        await setDoc(doc(db, settingsPath), { totalBudget: parseFloat(totalBudget) }, { merge: true });
        setIsEditingBudget(false);
    };

    const handleAddItem = async (e) => {
        e.preventDefault();
        if (!newItem.name) return;
        await addDoc(collection(db, itemsPath), {
            ...newItem,
            estimated: parseFloat(newItem.estimated) || 0,
            actual: parseFloat(newItem.actual) || 0,
            createdAt: serverTimestamp()
        });
        setNewItem({ name: '', category: 'General', estimated: '', actual: '', paid: false });
    };

    const handleDeleteItem = async (id) => {
        if(!window.confirm("Delete this item?")) return;
        await deleteDoc(doc(db, itemsPath, id));
    };

    const handleUpdateItem = async (id, field, value) => {
        await updateDoc(doc(db, itemsPath, id), { [field]: value });
    };

    const handleExportCSV = () => {
        if (items.length === 0) {
            alert("No budget items to export.");
            return;
        }
        const headers = "Item Name,Category,Estimated,Actual,Paid Status\n";
        const csvContent = headers + items.map(item => {
            return `"${(item.name || '').replace(/"/g, '""')}","${(item.category || '').replace(/"/g, '""')}","${item.estimated || 0}","${item.actual || 0}","${item.paid ? 'Yes' : 'No'}"`;
        }).join("\n");

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", `Budget_Expenses_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const generateAiBudget = async () => {
        if (!aiEventType || !aiGuestCount) return;
        setIsGenerating(true);
        
        try {
            const prompt = `I am planning a ${aiEventType} for ${aiGuestCount} people.
            Please generate a detailed, realistic budget breakdown for a ${aiBudgetLevel} tier event.
            Return strictly a JSON object with:
            1. "suggestedTotal": number (recommended total budget based on market rates)
            2. "items": array of objects { "category": string (choose from: Venue, Food & Drink, Decor, Entertainment, Attire, Photo/Video, Stationery, Favors, General), "name": string, "estimated": number }
            
            Do not include markdown formatting. Just the JSON.`;

            const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${GEMINI_API_KEY}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    contents: [{ role: "user", parts: [{ text: prompt }] }],
                    generationConfig: { responseMimeType: "application/json" }
                })
            });

            const result = await response.json();
            const text = result.candidates?.[0]?.content?.parts?.[0]?.text;
            
            if (text) {
                const data = JSON.parse(text);
                
                // Update total budget if not set or user confirms
                if (totalBudget === 0 || window.confirm(`Update total budget goal to suggested $${data.suggestedTotal}?`)) {
                    await setDoc(doc(db, settingsPath), { totalBudget: data.suggestedTotal }, { merge: true });
                }

                // Batch add items via Promise.all
                const promises = data.items.map(item => 
                    addDoc(collection(db, itemsPath), {
                        name: item.name,
                        category: item.category,
                        estimated: item.estimated,
                        actual: 0,
                        paid: false,
                        createdAt: serverTimestamp()
                    })
                );
                
                await Promise.all(promises);
                setShowAiModal(false);
            }
        } catch (error) {
            console.error("AI Budget Generation Error:", error);
            console.log("Failed to generate budget. Please try again.");
        } finally {
            setIsGenerating(false);
        }
    };

    const totalEstimated = items.reduce((acc, item) => acc + (item.estimated || 0), 0);
    const totalActual = items.reduce((acc, item) => acc + (item.actual || 0), 0);
    const totalPaid = items.filter(i => i.paid).reduce((acc, item) => acc + (item.actual || 0), 0);
    const remaining = totalBudget - totalActual;
    const percentUsed = totalBudget > 0 ? (totalActual / totalBudget) * 100 : 0;

    if (loading) return <div className="p-8 text-center text-gray-500">Loading budget...</div>;

    return (
        <div className="space-y-6 animate-fade-in">
            {/* AI Modal */}
            {showAiModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
                    <div className="bg-white p-6 rounded-xl shadow-xl max-w-md w-full">
                        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                            <span>🤖</span> AI Budget Generator {eventId ? '(Event Specific)' : ''}
                        </h3>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Event Type</label>
                                <select value={aiEventType} onChange={e => setAiEventType(e.target.value)} className="w-full p-2.5 border rounded-lg bg-white outline-none">
                                    <option value="">Select Event Type...</option>
                                    <option>Wedding</option>
                                    <option>Corporate Event</option>
                                    <option>Birthday Party</option>
                                    <option>Baby Shower</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Guest Count</label>
                                <input type="number" value={aiGuestCount} onChange={e => setAiGuestCount(e.target.value)} placeholder="e.g. 100" className="w-full p-2.5 border rounded-lg outline-none" />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Budget Level</label>
                                <select value={aiBudgetLevel} onChange={e => setAiBudgetLevel(e.target.value)} className="w-full p-2.5 border rounded-lg bg-white outline-none">
                                    <option>Budget</option>
                                    <option>Standard</option>
                                    <option>Luxury</option>
                                </select>
                            </div>
                            <button onClick={generateAiBudget} disabled={isGenerating} className="w-full bg-purple-600 text-white py-2 rounded-lg font-bold hover:bg-purple-700 disabled:opacity-50 mt-4">
                                {isGenerating ? 'Generating...' : 'Generate Budget Plan'}
                            </button>
                            <button onClick={() => setShowAiModal(false)} className="w-full bg-gray-200 text-gray-800 py-2 rounded-lg font-semibold hover:bg-gray-300">Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                    <p className="text-sm text-gray-500 font-bold uppercase">Total Budget</p>
                    {isEditingBudget ? (
                        <div className="flex gap-2 mt-1">
                            <input type="number" value={totalBudget} onChange={e => setTotalBudget(e.target.value)} className="w-full p-1 border rounded" />
                            <button onClick={handleUpdateBudget} className="text-green-600 font-bold">✓</button>
                        </div>
                    ) : (
                        <div className="flex justify-between items-center mt-1">
                            <p className="text-2xl font-bold text-gray-900">${parseFloat(totalBudget).toFixed(2)}</p>
                            <button onClick={() => setIsEditingBudget(true)} className="text-xs text-blue-500 hover:underline">Edit</button>
                        </div>
                    )}
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                    <p className="text-sm text-gray-500 font-bold uppercase">Actual Spent</p>
                    <p className={`text-2xl font-bold mt-1 ${totalActual > totalBudget ? 'text-red-600' : 'text-green-600'}`}>${totalActual.toFixed(2)}</p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                    <p className="text-sm text-gray-500 font-bold uppercase">Remaining</p>
                    <p className="text-2xl font-bold mt-1 text-gray-800">${remaining.toFixed(2)}</p>
                </div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                <div 
                    className={`h-4 rounded-full transition-all duration-500 ${percentUsed > 100 ? 'bg-red-500' : 'bg-green-500'}`} 
                    style={{ width: `${Math.min(percentUsed, 100)}%` }}
                ></div>
            </div>

            {/* Add Item Form */}
            <form onSubmit={handleAddItem} className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex flex-col md:flex-row gap-2 items-end">
                <div className="flex-grow w-full">
                    <label className="text-xs font-bold text-gray-500 ml-1">Item Name</label>
                    <input type="text" placeholder="e.g. Catering" value={newItem.name} onChange={e => setNewItem({...newItem, name: e.target.value})} className="w-full p-2 border rounded-md" />
                </div>
                <div className="w-full md:w-32">
                    <label className="text-xs font-bold text-gray-500 ml-1">Category</label>
                    <select value={newItem.category} onChange={e => setNewItem({...newItem, category: e.target.value})} className="w-full p-2 border rounded-md bg-white">
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
                <div className="w-full md:w-24">
                    <label className="text-xs font-bold text-gray-500 ml-1">Est. ($)</label>
                    <input type="number" placeholder="0.00" value={newItem.estimated} onChange={e => setNewItem({...newItem, estimated: e.target.value})} className="w-full p-2 border rounded-md" />
                </div>
                <button type="submit" className="bg-pink-500 text-white px-4 py-2 rounded-md font-bold hover:bg-pink-600 h-[42px] w-full md:w-auto">Add</button>
            </form>

            {/* Items List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="flex justify-between items-center p-4 border-b bg-gray-50">
                    <h3 className="font-bold text-gray-800">Budget Breakdown</h3>
                    <div className="flex gap-2">
                        <button onClick={handleExportCSV} className="text-sm bg-white border border-gray-300 text-gray-700 font-bold flex items-center gap-1 hover:bg-gray-50 px-2 py-1 rounded shadow-sm">
                            <span>⬇️</span> Export CSV
                        </button>
                        <button onClick={() => setShowAiModal(true)} className="text-sm text-purple-600 font-bold flex items-center gap-1 hover:bg-purple-50 px-2 py-1 rounded">
                            <span>✨</span> AI Suggest
                        </button>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-white border-b border-gray-200 text-xs uppercase font-bold text-gray-400 tracking-wider">
                            <tr>
                                <th className="px-4 py-4">Item</th>
                                <th className="px-4 py-4">Category</th>
                                <th className="px-4 py-4 text-right">Estimated</th>
                                <th className="px-4 py-4 text-right">Actual</th>
                                <th className="px-4 py-4 text-center">Paid</th>
                                <th className="px-4 py-4"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {items.map(item => (
                                <tr key={item.id} className="hover:bg-gray-50 transition">
                                    <td className="px-4 py-3 font-medium text-gray-900">{item.name}</td>
                                    <td className="px-4 py-3 text-gray-500">{item.category}</td>
                                    <td className="px-4 py-3 text-right">
                                        <input type="number" value={item.estimated || ''} onChange={e => handleUpdateItem(item.id, 'estimated', e.target.value)} className="w-20 text-right p-1 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-pink-500 outline-none" />
                                    </td>
                                    <td className="px-4 py-3 text-right font-medium">
                                        <input type="number" value={item.actual || ''} onChange={e => handleUpdateItem(item.id, 'actual', e.target.value)} className="w-20 text-right p-1 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-pink-500 outline-none font-bold" />
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <input type="checkbox" checked={item.paid} onChange={e => handleUpdateItem(item.id, 'paid', e.target.checked)} className="w-4 h-4 text-pink-600 rounded focus:ring-pink-500 cursor-pointer" />
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => handleDeleteItem(item.id)} className="text-gray-400 hover:text-red-600 font-bold p-1 rounded-full hover:bg-red-50 transition">✕</button>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && (
                                <tr><td colSpan="6" className="p-8 text-center text-gray-400 italic">No budget items added yet.</td></tr>
                            )}
                        </tbody>
                        <tfoot className="bg-gray-50 font-bold text-gray-800">
                            <tr>
                                <td colSpan="2" className="p-3 text-right">Totals:</td>
                                <td className="p-3 text-right">${totalEstimated.toFixed(2)}</td>
                                <td className="p-3 text-right">${totalActual.toFixed(2)}</td>
                                <td colSpan="2"></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
    );
}